import React, { useState, useEffect, useRef, useCallback, useLayoutEffect, memo } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown, { defaultUrlTransform } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  ArrowUp, Square, Copy, Check, RotateCcw, ArrowDown, Phone, User, ChevronDown,
  MapPin, Navigation, Mail, Globe, ArrowUpRight,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { API_URL } from '../utils/api';
import './ChatInterface.css';

const COPY = {
  fa: {
    placeholder: 'پیام خود را بنویسید…',
    thinking: 'در حال فکر کردن',
    online: 'آنلاین',
    newChat: 'گفتگوی جدید',
    copy: 'کپی',
    copied: 'کپی شد',
    stop: 'توقف',
    send: 'ارسال',
    scrollDown: 'رفتن به آخرین پیام',
    moreQuestions: 'سوالات بیشتر',
    disclaimer: 'پاسخ‌ها با هوش مصنوعی تولید می‌شوند و ممکن است خطا داشته باشند.',
    fallback: 'لحظه‌ای مشکل کوچکی داشتم، اما الان آماده‌ام. لطفاً دوباره بپرسید.',
    loading: 'در حال بارگذاری…',
    notFoundTitle: 'دستیار پیدا نشد',
    notFoundBody: 'این لینک معتبر نیست یا دستیار حذف شده است.',
    formTitle: 'قبل از شروع',
    formBody: 'برای پاسخ دقیق‌تر، اطلاعات خود را وارد کنید.',
    firstName: 'نام',
    lastName: 'نام خانوادگی',
    phone: 'شماره تماس',
    formSubmit: 'شروع گفتگو',
    formMissing: 'لطفاً تمام فیلدها را پر کنید',
  },
  en: {
    placeholder: 'Message…',
    thinking: 'Thinking',
    online: 'Online',
    newChat: 'New chat',
    copy: 'Copy',
    copied: 'Copied',
    stop: 'Stop',
    send: 'Send',
    scrollDown: 'Scroll to latest',
    moreQuestions: 'More questions',
    disclaimer: 'Answers are AI-generated and may contain mistakes.',
    fallback: "I had a small hiccup there. Please ask again.",
    loading: 'Loading…',
    notFoundTitle: 'Assistant not found',
    notFoundBody: 'This link is invalid or the assistant was removed.',
    formTitle: 'Before we start',
    formBody: 'Share a few details so we can help you better.',
    firstName: 'First name',
    lastName: 'Last name',
    phone: 'Phone number',
    formSubmit: 'Start chatting',
    formMissing: 'Please fill in all fields',
  },
};

const SUGGESTION_PREVIEW = 4;

let idSeed = 0;
const nextId = () => `m${Date.now().toString(36)}${(idSeed++).toString(36)}`;

const newSessionId = () => `session_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`;
const sessionKey = (slug) => `bc-session:${slug}`;

const readSessionId = (slug) => {
  try {
    return window.localStorage.getItem(sessionKey(slug));
  } catch (e) {
    return null;
  }
};

const storeSessionId = (slug, id) => {
  try {
    window.localStorage.setItem(sessionKey(slug), id);
  } catch (e) { /* private mode: memory still works for this tab */ }
};

const ensureSessionId = (slug) => {
  const existing = readSessionId(slug);
  if (existing) return existing;
  const id = newSessionId();
  storeSessionId(slug, id);
  return id;
};

const isCoarsePointer = () =>
  typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(pointer: coarse)').matches;

// ── Rich links ─────────────────────────────────────────────────────────────

const LINK_COPY = {
  fa: { directions: 'مسیریابی', openIn: (name) => `باز کردن در ${name}`, call: 'تماس', email: 'ایمیل' },
  en: { directions: 'Directions', openIn: (name) => `Open in ${name}`, call: 'Call', email: 'Email' },
};

const MAP_PROVIDERS = [
  { id: 'neshan', fa: 'نشان', en: 'Neshan', match: (h) => h === 'neshan.org' || h.endsWith('.neshan.org') || h === 'nshn.ir' },
  { id: 'balad', fa: 'بلد', en: 'Balad', match: (h) => h === 'balad.ir' || h.endsWith('.balad.ir') },
  {
    id: 'google',
    fa: 'گوگل مپ',
    en: 'Google Maps',
    match: (h, u) => h === 'maps.google.com' || h === 'maps.app.goo.gl'
      || (h === 'goo.gl' && u.pathname.startsWith('/maps'))
      || (/(^|\.)google\.[a-z.]+$/.test(h) && u.pathname.startsWith('/maps')),
  },
  { id: 'waze', fa: 'ویز', en: 'Waze', match: (h) => h === 'waze.com' || h.endsWith('.waze.com') },
  { id: 'osm', fa: 'OpenStreetMap', en: 'OpenStreetMap', match: (h) => h.endsWith('openstreetmap.org') },
];

const parseUrl = (href) => {
  try {
    return new URL(href);
  } catch (e) {
    return null;
  }
};

const hostOf = (u) => u.hostname.replace(/^www\./, '');

const findCoords = (href) => {
  const m = decodeURIComponent(href).match(/(-?\d{1,2}\.\d{2,})\s*[,-]\s*(-?\d{1,3}\.\d{2,})/);
  if (!m) return null;
  const lat = Number(m[1]);
  const lng = Number(m[2]);
  return Math.abs(lat) <= 90 && Math.abs(lng) <= 180 ? { lat, lng } : null;
};

const textOf = (children) => React.Children.toArray(children)
  .map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : textOf(c?.props?.children)))
  .join('');

const DIGITS = '[0-9\u06F0-\u06F9]';
const PHONE_RE = new RegExp(
  `(?<![\\w/#.=?&:\\[\\-\u06F0-\u06F9])(?<!\\]\\()(?:\\+98[\\s-]?|[0\u06F0])${DIGITS}{2}[\\s-]?${DIGITS}{3,4}[\\s-]?${DIGITS}{4}(?![\\w\u06F0-\u06F9])`,
  'g'
);

const toLatinDigits = (s) => s.replace(/[\u06F0-\u06F9]/g, (d) => String(d.charCodeAt(0) - 0x06f0));

// Turns plain phone numbers into tel: links so they render as tap-to-call chips.
const linkifyPhones = (text) => text.replace(PHONE_RE, (num) => `[${num}](tel:${toLatinDigits(num).replace(/[\s-]/g, '')})`);

const MapCard = ({ href, provider, lang }) => {
  const L = LINK_COPY[lang];
  const coords = findCoords(href);
  const alternatives = coords
    ? [
      { id: 'google', name: lang === 'fa' ? 'گوگل مپ' : 'Google Maps', url: `https://www.google.com/maps/search/?api=1&query=${coords.lat},${coords.lng}` },
      { id: 'waze', name: lang === 'fa' ? 'ویز' : 'Waze', url: `https://waze.com/ul?ll=${coords.lat},${coords.lng}&navigate=yes` },
    ].filter((a) => a.id !== provider.id)
    : [];

  return (
    <span className="bc-place">
      <span className="bc-place-head">
        <span className="bc-place-icon"><MapPin size={18} strokeWidth={2} /></span>
        <span className="bc-place-text">
          <span className="bc-place-title">{L.directions}</span>
          <span className="bc-place-sub">{L.openIn(provider[lang])}</span>
        </span>
      </span>
      <span className="bc-place-actions">
        <a className="bc-place-btn is-primary" href={href} target="_blank" rel="noopener noreferrer">
          <Navigation size={14} strokeWidth={2.2} />
          {provider[lang]}
        </a>
        {alternatives.map((a) => (
          <a key={a.id} className="bc-place-btn" href={a.url} target="_blank" rel="noopener noreferrer">{a.name}</a>
        ))}
      </span>
    </span>
  );
};

const RichLink = ({ href = '', children, lang }) => {
  const label = textOf(children).trim();

  if (href.startsWith('tel:')) {
    return (
      <a className="bc-pill bc-pill--action" href={href} dir="ltr" aria-label={`${LINK_COPY[lang].call} ${label}`}>
        <Phone size={13} strokeWidth={2.2} />
        <span>{toLatinDigits(label) || href.slice(4)}</span>
      </a>
    );
  }

  if (href.startsWith('mailto:')) {
    return (
      <a className="bc-pill" href={href} dir="ltr" aria-label={`${LINK_COPY[lang].email} ${label}`}>
        <Mail size={13} strokeWidth={2.2} />
        <span>{label || href.slice(7)}</span>
      </a>
    );
  }

  const url = parseUrl(href);
  if (!url) return <a href={href} target="_blank" rel="noopener noreferrer">{children}</a>;

  const host = hostOf(url);
  const provider = MAP_PROVIDERS.find((p) => p.match(host, url));
  if (provider) return <MapCard href={href} provider={provider} lang={lang} />;

  const isBareUrl = !label || label === href || label.replace(/\/$/, '') === href.replace(/\/$/, '') || /^https?:\/\//.test(label);
  if (isBareUrl) {
    return (
      <a className="bc-pill" href={href} target="_blank" rel="noopener noreferrer" dir="ltr" title={href}>
        <Globe size={13} strokeWidth={2} />
        <span>{host}</span>
      </a>
    );
  }

  return (
    <a className="bc-link" href={href} target="_blank" rel="noopener noreferrer" title={host}>
      {children}
      <ArrowUpRight size={13} strokeWidth={2.2} className="bc-link-icon" />
    </a>
  );
};

const componentCache = {};
const markdownComponentsFor = (lang) => {
  if (!componentCache[lang]) {
    componentCache[lang] = {
      a: ({ href, children }) => <RichLink href={href} lang={lang}>{children}</RichLink>,
      table: ({ children }) => (
        <div className="bc-table-wrap"><table>{children}</table></div>
      ),
    };
  }
  return componentCache[lang];
};

const urlTransform = (url) => (/^tel:\+?\d+$/.test(url) ? url : defaultUrlTransform(url));

const Markdown = memo(({ text, lang = 'fa' }) => (
  <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponentsFor(lang)} urlTransform={urlTransform}>
    {linkifyPhones(text)}
  </ReactMarkdown>
));

// Reveals incoming text at a steady, adaptive pace so bursty network chunks read like smooth typing.
function useSmoothText(target, animate) {
  const [shown, setShown] = useState(animate ? '' : target);
  const shownLen = useRef(animate ? 0 : target.length);

  useEffect(() => {
    if (!animate) {
      shownLen.current = target.length;
      setShown(target);
      return undefined;
    }
    let raf;
    const tick = () => {
      const remaining = target.length - shownLen.current;
      if (remaining <= 0) return;
      const step = Math.max(2, Math.ceil(remaining / 10));
      shownLen.current = Math.min(target.length, shownLen.current + step);
      setShown(target.slice(0, shownLen.current));
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, animate]);

  return shown;
}

const scriptLang = (text, fallback) => {
  const persian = (text.match(/[\u0600-\u06FF]/g) || []).length;
  const latin = (text.match(/[A-Za-z]/g) || []).length;
  if (!persian && !latin) return fallback;
  return persian >= latin * 0.5 ? 'fa' : 'en';
};

const AssistantText = ({ text, streaming, animate, t, lang: botLang }) => {
  const shown = useSmoothText(text, animate);
  const lang = scriptLang(text.slice(0, 400), botLang);
  const revealing = streaming || shown.length < text.length;

  if (!text && streaming) {
    return (
      <div className="bc-thinking" role="status">
        <span className="bc-thinking-dot" />
        <span className="bc-thinking-text">{t.thinking}</span>
      </div>
    );
  }

  return (
    <div className={`bc-md${revealing ? ' is-streaming' : ''}`} dir={lang === 'fa' ? 'rtl' : 'ltr'} lang={lang}>
      <Markdown text={shown} lang={lang} />
    </div>
  );
};

const CopyButton = ({ text, t }) => {
  const [copied, setCopied] = useState(false);
  const onCopy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch (e) {
      toast.error('Copy failed');
    }
  };
  return (
    <button type="button" className="bc-action" onClick={onCopy} aria-label={copied ? t.copied : t.copy}>
      {copied ? <Check size={15} strokeWidth={2.2} /> : <Copy size={15} strokeWidth={2} />}
      <span className="bc-action-label">{copied ? t.copied : t.copy}</span>
    </button>
  );
};

const BotAvatar = ({ src, name, size = 'sm' }) => {
  const [failed, setFailed] = useState(false);
  const initial = (name || '?').trim().charAt(0);
  return (
    <span className={`bc-avatar bc-avatar--${size}`} aria-hidden="true">
      {src && !failed ? (
        <img src={src} alt="" onError={() => setFailed(true)} draggable="false" />
      ) : (
        <span className="bc-avatar-initial">{initial}</span>
      )}
    </span>
  );
};

const ChatInterface = () => {
  const { slug } = useParams();
  const [chatbot, setChatbot] = useState(null);
  const [accent, setAccent] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [sessionId, setSessionId] = useState(() => ensureSessionId(slug));
  const [showUserForm, setShowUserForm] = useState(false);
  const [userInfo, setUserInfo] = useState({ name: '', family: '', phone: '' });
  const [showAllSuggestions, setShowAllSuggestions] = useState(false);
  const [atBottom, setAtBottom] = useState(true);
  const [scrolled, setScrolled] = useState(false);

  const rootRef = useRef(null);
  const threadRef = useRef(null);
  const inputRef = useRef(null);
  const abortRef = useRef(null);
  const stickToBottom = useRef(true);

  // The bot's manifest, icons and favicon are injected by the server at /chat/<slug>/
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' || !slug) return;
    const appPath = `/chat/${encodeURIComponent(slug)}/`;
    if (window.location.pathname !== appPath) {
      window.location.replace(`${appPath}${window.location.search}`);
    }
  }, [slug]);

  const iconSrc = slug ? `/bot-assets/${encodeURIComponent(slug)}/icon-192.png` : null;

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const sid = ensureSessionId(slug);
    setSessionId(sid);
    const restore = axios
      .get(`${API_URL}/chatbots/${slug}/sessions/${encodeURIComponent(sid)}/messages`)
      .then(({ data }) => (Array.isArray(data.messages) ? data.messages : []))
      .catch(() => []);

    Promise.all([axios.get(`${API_URL}/chatbots/${slug}/public`), restore])
      .then(([{ data }, saved]) => {
        if (cancelled) return;
        setChatbot(data);
        const greeting = data.customGreeting
          ? [{ id: nextId(), sender: 'bot', text: data.customGreeting, animate: saved.length === 0 }]
          : [];
        const restored = saved
          .filter((m) => m.text)
          .map((m) => ({ id: nextId(), sender: m.sender, text: m.text, reply: m.sender === 'bot' }));
        setMessages([...greeting, ...restored]);
        if (data.collectUserInfo && restored.length === 0) setShowUserForm(true);
      })
      .catch(() => { if (!cancelled) setChatbot(null); })
      .finally(() => { if (!cancelled) setLoading(false); });

    fetch(`/bot-assets/${encodeURIComponent(slug)}/meta.json`)
      .then((r) => (r.ok ? r.json() : null))
      .then((meta) => { if (!cancelled && meta && meta.theme_color) setAccent(meta.theme_color); })
      .catch(() => {});

    return () => { cancelled = true; };
  }, [slug]);

  // Track the visual viewport so the composer rides above the mobile keyboard.
  useEffect(() => {
    const vv = window.visualViewport;
    const apply = () => {
      const h = vv ? vv.height : window.innerHeight;
      if (rootRef.current) rootRef.current.style.setProperty('--bc-app-h', `${h}px`);
      if (vv && rootRef.current) rootRef.current.style.setProperty('--bc-app-top', `${vv.offsetTop}px`);
    };
    apply();
    const target = vv || window;
    target.addEventListener('resize', apply);
    if (vv) vv.addEventListener('scroll', apply);
    return () => {
      target.removeEventListener('resize', apply);
      if (vv) vv.removeEventListener('scroll', apply);
    };
  }, [loading]);

  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const scrollToBottom = useCallback((behavior = 'auto') => {
    const el = threadRef.current;
    if (!el) return;
    el.scrollTo({ top: el.scrollHeight, behavior });
  }, []);

  const lastScrollTop = useRef(0);

  // Only an upward scroll by the reader detaches; programmatic scrolls always move down.
  const onThreadScroll = () => {
    const el = threadRef.current;
    if (!el) return;
    const distance = el.scrollHeight - el.scrollTop - el.clientHeight;
    const nearBottom = distance < 80;
    if (nearBottom) stickToBottom.current = true;
    else if (el.scrollTop < lastScrollTop.current - 2) stickToBottom.current = false;
    lastScrollTop.current = el.scrollTop;
    setAtBottom(nearBottom || stickToBottom.current);
    setScrolled(el.scrollTop > 4);
  };

  // Keep following the reply while it grows, unless the reader scrolled up.
  useEffect(() => {
    const el = threadRef.current;
    if (!el || typeof ResizeObserver === 'undefined') return undefined;
    const content = el.firstElementChild;
    if (!content) return undefined;
    const ro = new ResizeObserver(() => {
      if (stickToBottom.current) el.scrollTop = el.scrollHeight;
    });
    ro.observe(content);
    return () => ro.disconnect();
  }, [loading, chatbot]);

  const autoGrow = () => {
    const el = inputRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 168)}px`;
  };

  useLayoutEffect(autoGrow, [input]);

  const lang = chatbot && chatbot.language === 'fa' ? 'fa' : 'en';
  const t = COPY[lang];
  const isRTL = lang === 'fa';

  const updateMessage = (id, patch) =>
    setMessages((prev) => prev.map((m) => (m.id === id ? { ...m, ...(typeof patch === 'function' ? patch(m) : patch) } : m)));

  const runStream = async (msg, history, botId, attempt = 0) => {
    const controller = new AbortController();
    abortRef.current = controller;
    let aiText = '';

    try {
      const response = await fetch(`${API_URL}/chatbots/${slug}/stream`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        signal: controller.signal,
        body: JSON.stringify({
          message: msg,
          sessionId,
          history,
          ...(chatbot.collectUserInfo && !showUserForm && userInfo.name ? { userInfo } : {}),
        }),
      });
      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          const data = line.substring(6).trim();
          if (data === 'END') {
            updateMessage(botId, { text: aiText, streaming: false });
            return;
          }
          try {
            const content = JSON.parse(data);
            const piece = typeof content === 'string' ? content : (content && typeof content.content === 'string' ? content.content : '');
            if (piece) {
              aiText += piece;
              updateMessage(botId, { text: aiText });
            }
          } catch (e) { /* ignore keep-alive and partial frames */ }
        }
      }
      updateMessage(botId, { text: aiText || t.fallback, streaming: false });
    } catch (error) {
      if (controller.signal.aborted) {
        updateMessage(botId, { text: aiText, streaming: false, stopped: true });
        return;
      }
      if (attempt < 2 && !aiText) {
        await new Promise((r) => setTimeout(r, 900));
        return runStream(msg, history, botId, attempt + 1);
      }
      updateMessage(botId, { text: aiText || t.fallback, streaming: false });
    } finally {
      if (abortRef.current === controller) abortRef.current = null;
    }
  };

  const send = async (raw) => {
    const msg = (raw || '').trim();
    if (!msg || busy || showUserForm) return;

    const history = messages
      .filter((m) => m.text)
      .map((m) => ({ role: m.sender === 'user' ? 'user' : 'assistant', content: m.text }))
      .concat([{ role: 'user', content: msg }]);

    const botId = nextId();
    setMessages((prev) => [
      ...prev,
      { id: nextId(), sender: 'user', text: msg },
      { id: botId, sender: 'bot', text: '', streaming: true, animate: true, reply: true },
    ]);
    setInput('');
    setBusy(true);
    stickToBottom.current = true;
    requestAnimationFrame(() => scrollToBottom('smooth'));

    await runStream(msg, history, botId);
    setBusy(false);
  };

  const stop = () => abortRef.current && abortRef.current.abort();

  const onSubmit = (e) => {
    e.preventDefault();
    send(input);
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey && !e.nativeEvent.isComposing && !isCoarsePointer()) {
      e.preventDefault();
      send(input);
    }
  };

  const newChat = () => {
    if (abortRef.current) abortRef.current.abort();
    setBusy(false);
    const fresh = newSessionId();
    storeSessionId(slug, fresh);
    setSessionId(fresh);
    setShowAllSuggestions(false);
    setMessages(chatbot && chatbot.customGreeting
      ? [{ id: nextId(), sender: 'bot', text: chatbot.customGreeting, animate: false }]
      : []);
    setInput('');
  };

  const submitUserInfo = (e) => {
    e.preventDefault();
    if (!userInfo.name.trim() || !userInfo.family.trim() || !userInfo.phone.trim()) {
      toast.error(t.formMissing);
      return;
    }
    setShowUserForm(false);
    requestAnimationFrame(() => inputRef.current && inputRef.current.focus());
  };

  const rootStyle = accent || (chatbot && chatbot.customColors && chatbot.customColors.primary)
    ? { '--bc-accent': accent || chatbot.customColors.primary }
    : undefined;

  if (loading) {
    return (
      <div className="bc-app bc-app--center" ref={rootRef}>
        <span className="bc-spinner" aria-label="Loading" />
      </div>
    );
  }

  if (!chatbot) {
    return (
      <div className="bc-app bc-app--center" ref={rootRef}>
        <div className="bc-empty-state">
          <h1>{COPY.fa.notFoundTitle}</h1>
          <p>{COPY.fa.notFoundBody}</p>
          <p dir="ltr">{COPY.en.notFoundTitle}</p>
        </div>
      </div>
    );
  }

  const userCount = messages.filter((m) => m.sender === 'user').length;
  const suggestions = chatbot.quickQuestions || [];
  const visibleSuggestions = showAllSuggestions ? suggestions : suggestions.slice(0, SUGGESTION_PREVIEW);
  const showWelcome = userCount === 0;

  return (
    <div className="bc-app" ref={rootRef} dir={isRTL ? 'rtl' : 'ltr'} lang={lang} style={rootStyle}>
      <header className={`bc-header${scrolled ? ' is-scrolled' : ''}`}>
        <div className="bc-header-inner">
          <BotAvatar src={iconSrc || chatbot.customLogo} name={chatbot.name} size="md" />
          <div className="bc-header-text">
            <h1 className="bc-title">{chatbot.name}</h1>
            <p className="bc-status"><span className="bc-status-dot" />{t.online}</p>
          </div>
          <button
            type="button"
            className="bc-icon-btn"
            onClick={newChat}
            disabled={userCount === 0}
            aria-label={t.newChat}
            title={t.newChat}
          >
            <RotateCcw size={18} strokeWidth={2} />
          </button>
        </div>
      </header>

      <main className="bc-thread" ref={threadRef} onScroll={onThreadScroll}>
        <div className="bc-thread-inner">
          {showWelcome && (
            <section className="bc-hero">
              <BotAvatar src={iconSrc || chatbot.customLogo} name={chatbot.name} size="lg" />
              <h2 className="bc-hero-title">{chatbot.name}</h2>
              {chatbot.description && <p className="bc-hero-desc">{chatbot.description}</p>}
            </section>
          )}

          {messages.map((m) => (
            m.sender === 'user' ? (
              <div key={m.id} className="bc-msg bc-msg--user">
                <div className="bc-bubble" dir="auto">{m.text}</div>
              </div>
            ) : (
              <div key={m.id} className="bc-msg bc-msg--bot">
                <AssistantText text={m.text} streaming={!!m.streaming} animate={!!m.animate} t={t} lang={lang} />
                {m.reply && !m.streaming && m.text && (
                  <div className="bc-actions">
                    <CopyButton text={m.text} t={t} />
                  </div>
                )}
              </div>
            )
          ))}

          {showWelcome && suggestions.length > 0 && (
            <section className="bc-suggestions" aria-label={t.moreQuestions}>
              {visibleSuggestions.map((q, i) => (
                <button
                  key={q}
                  type="button"
                  className="bc-suggestion"
                  style={{ '--i': i }}
                  onClick={() => send(q)}
                  disabled={busy || showUserForm}
                >
                  <span>{q}</span>
                  <ArrowUp size={16} strokeWidth={2} className="bc-suggestion-icon" />
                </button>
              ))}
              {suggestions.length > SUGGESTION_PREVIEW && !showAllSuggestions && (
                <button type="button" className="bc-more" onClick={() => setShowAllSuggestions(true)}>
                  {t.moreQuestions}
                  <ChevronDown size={16} strokeWidth={2} />
                </button>
              )}
            </section>
          )}
        </div>
      </main>

      <footer className="bc-dock">
        <button
          type="button"
          className={`bc-jump${atBottom ? '' : ' is-visible'}`}
          onClick={() => { stickToBottom.current = true; scrollToBottom('smooth'); }}
          aria-label={t.scrollDown}
          tabIndex={atBottom ? -1 : 0}
        >
          <ArrowDown size={18} strokeWidth={2} />
        </button>

        {!showWelcome && suggestions.length > 0 && (
          <div className="bc-chips" role="list">
            {suggestions.map((q) => (
              <button key={q} type="button" role="listitem" className="bc-chip" onClick={() => send(q)} disabled={busy}>
                {q}
              </button>
            ))}
          </div>
        )}

        <form className="bc-composer" onSubmit={onSubmit}>
          <textarea
            ref={inputRef}
            rows={1}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder={t.placeholder}
            disabled={showUserForm}
            dir="auto"
            enterKeyHint="send"
            aria-label={t.placeholder}
            className="bc-input"
          />
          {busy ? (
            <button type="button" className="bc-send is-stop" onClick={stop} aria-label={t.stop} title={t.stop}>
              <Square size={14} strokeWidth={0} fill="currentColor" />
            </button>
          ) : (
            <button type="submit" className="bc-send" disabled={!input.trim() || showUserForm} aria-label={t.send} title={t.send}>
              <ArrowUp size={20} strokeWidth={2.4} />
            </button>
          )}
        </form>
        <p className="bc-disclaimer">{t.disclaimer}</p>
      </footer>

      {showUserForm && (
        <div className="bc-sheet-backdrop">
          <form className="bc-sheet" onSubmit={submitUserInfo}>
            <span className="bc-sheet-grip" aria-hidden="true" />
            <h3 className="bc-sheet-title">{t.formTitle}</h3>
            <p className="bc-sheet-body">{t.formBody}</p>
            <label className="bc-field">
              <span><User size={15} strokeWidth={2} />{t.firstName}</span>
              <input value={userInfo.name} onChange={(e) => setUserInfo((p) => ({ ...p, name: e.target.value }))} autoComplete="given-name" />
            </label>
            <label className="bc-field">
              <span><User size={15} strokeWidth={2} />{t.lastName}</span>
              <input value={userInfo.family} onChange={(e) => setUserInfo((p) => ({ ...p, family: e.target.value }))} autoComplete="family-name" />
            </label>
            <label className="bc-field">
              <span><Phone size={15} strokeWidth={2} />{t.phone}</span>
              <input type="tel" inputMode="tel" dir="ltr" value={userInfo.phone} onChange={(e) => setUserInfo((p) => ({ ...p, phone: e.target.value }))} autoComplete="tel" />
            </label>
            <button type="submit" className="bc-primary">{t.formSubmit}</button>
          </form>
        </div>
      )}
    </div>
  );
};

export default ChatInterface;
